import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { getPaymentStatus } from "../../../api/bseApi";

const POLL_INTERVAL_MS = 60000; // 1 minute for PENDING

/**
 * Parse BSE payment status response into structured data
 */
function parsePaymentStatusResponse(rawData) {
  if (!rawData || typeof rawData !== "string") {
    return { status: "UNKNOWN", transactionRef: "", remarks: "", lastUpdated: "" };
  }
  const parts = rawData.split("|").map((p) => (p || "").trim());
  const upper = rawData.toUpperCase();
  let status = "UNKNOWN";
  if (upper.includes("SUCCESS") || upper.includes("PAID") || upper.includes("COMPLETED")) status = "SUCCESS";
  else if (upper.includes("PENDING") || upper.includes("PROCESSING") || upper.includes("WAITING")) status = "PENDING";
  else if (upper.includes("FAILED") || upper.includes("REJECTED") || upper.includes("ERROR")) status = "FAILED";

  return {
    status,
    transactionRef: parts[1] || parts[0] || "",
    remarks: parts[2] || parts[1] || rawData,
    lastUpdated: parts[3] || parts[2] || new Date().toLocaleString(),
  };
}

/**
 * PaymentStatusScreen - BSE StAR MF Client Order Payment Status (MFAPI Flag 11)
 * Opens after redirect from Payment Gateway or from Order History
 * Receives: OrderNo, ClientCode
 */
export default function PaymentStatusScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [retryAmount, setRetryAmount] = useState("");

  const orderNo = state?.orderNo || searchParams.get("orderNo");
  const clientCode = state?.clientCode || searchParams.get("clientCode");

  const resolvedOrderNo = context?.orderNo || orderNo;
  const resolvedClientCode = context?.clientCode || clientCode;

  const fetchStatus = useCallback(async () => {
    if (!resolvedOrderNo || !resolvedClientCode) return null;
    try {
      const res = await getPaymentStatus({ clientCode: resolvedClientCode, orderNo: resolvedOrderNo, segment: "MF" });
      if (res.data?.status === 1) {
        const parsed = parsePaymentStatusResponse(res.data?.data);
        return { raw: res.data?.data, ...parsed };
      }
      return { status: "ERROR", remarks: res.data?.message || res.data?.error || "API error" };
    } catch (err) {
      return { status: "ERROR", remarks: err.response?.data?.message || err.message || "Fetch failed" };
    }
  }, [resolvedOrderNo, resolvedClientCode]);

  useEffect(() => {
    if (orderNo && clientCode) {
      setContext({ orderNo, clientCode });
      setError(null);
    } else if (!context) {
      setError("Missing OrderNo or ClientCode. Open from Payment Gateway return or Order History.");
    }
  }, [orderNo, clientCode]);

  useEffect(() => {
    if (!resolvedOrderNo || !resolvedClientCode) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchStatus();
      setLoading(false);
      if (result) {
        setPaymentData(result);
        if (result.status === "PENDING") setPolling(true);
      } else {
        setError("Could not fetch payment status.");
      }
    };

    load();
  }, [resolvedOrderNo, resolvedClientCode, fetchStatus]);

  useEffect(() => {
    if (!polling || !paymentData || paymentData.status !== "PENDING") return;
    const timer = setInterval(async () => {
      const result = await fetchStatus();
      if (result) {
        setPaymentData(result);
        if (result.status !== "PENDING") setPolling(false);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [polling, paymentData?.status, fetchStatus]);

  const handleRefresh = async () => {
    if (!context) return;
    setLoading(true);
    const result = await fetchStatus();
    setLoading(false);
    if (result) setPaymentData(result);
  };

  const handleRetryPayment = () => {
    const amt = parseFloat(retryAmount || state?.amount || searchParams.get("amount") || "");
    if (!amt || amt <= 0) {
      Swal.fire("Amount Required", "Enter payable amount for retry payment.", "warning");
      return;
    }
    navigate("/payment/mode", {
      state: {
        orderNo: resolvedOrderNo,
        clientCode: resolvedClientCode,
        amount: amt,
      },
    });
  };

  const [manualOrderNo, setManualOrderNo] = useState(orderNo || "");
  const [manualClientCode, setManualClientCode] = useState(clientCode || "");
  const hasParams = orderNo && clientCode;
  const showManualEntry = !hasParams && !context;

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const no = manualOrderNo?.trim();
    const cc = manualClientCode?.trim();
    if (!no || !cc) {
      Swal.fire("Required", "Enter Order No and Client Code.", "warning");
      return;
    }
    setContext({ orderNo: no, clientCode: cc });
    setManualOrderNo("");
    setManualClientCode("");
  };

  if (showManualEntry) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content">
            <div className="mb-4" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: "20px" }}>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                  <li className="breadcrumb-item active">Payment Status</li>
                </ol>
              </nav>
              <h5>Payment Status</h5>
              <p className="text-muted mb-3">Enter order details to check payment status (from Order History or Gateway return).</p>
              <form onSubmit={handleManualSubmit} className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Order No <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={manualOrderNo} onChange={(e) => setManualOrderNo(e.target.value)} placeholder="BSE Order Number" required />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Client Code <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={manualClientCode} onChange={(e) => setManualClientCode(e.target.value)} placeholder="UCC / Client Code" required />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button type="submit" className="btn btn-primary me-2">Fetch Status</button>
                  <Link to="/user" className="btn btn-outline-secondary">Cancel</Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!context && !hasParams) return null;
  if (!resolvedOrderNo || !resolvedClientCode) return null;

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Payment Status</li>
              </ol>
            </nav>
            <h5>Payment Status</h5>
            <p className="text-muted mb-0">
              Order No: <strong>{resolvedOrderNo}</strong> | Client: <strong>{resolvedClientCode}</strong>
            </p>
          </div>

          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
              Payment Status
            </h6>

            {loading && !paymentData && (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-lg text-primary mb-3" role="status" />
                <p>Fetching payment status...</p>
              </div>
            )}

            {error && !paymentData && (
              <div className="alert alert-danger">
                <i className="fas fa-times-circle me-2" />
                {error}
                <div className="mt-3">
                  <button className="btn btn-outline-primary btn-sm me-2" onClick={handleRefresh}>
                    Retry
                  </button>
                  <Link to="/user" className="btn btn-outline-secondary btn-sm">Back to Users</Link>
                </div>
              </div>
            )}

            {paymentData && (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label text-muted">Order Number</label>
                    <p className="form-control-plaintext fw-bold">{resolvedOrderNo}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted">Payment Status</label>
                    <p className="mb-0">
                      {paymentData.status === "SUCCESS" && (
                        <span className="badge bg-success fs-6">
                          <i className="fas fa-check-circle me-1" /> SUCCESS
                        </span>
                      )}
                      {paymentData.status === "PENDING" && (
                        <span className="badge bg-warning text-dark fs-6">
                          <i className="fas fa-clock me-1" /> PENDING
                        </span>
                      )}
                      {(paymentData.status === "FAILED" || paymentData.status === "ERROR") && (
                        <span className="badge bg-danger fs-6">
                          <i className="fas fa-times-circle me-1" /> {paymentData.status}
                        </span>
                      )}
                      {paymentData.status === "UNKNOWN" && (
                        <span className="badge bg-secondary fs-6">{paymentData.status}</span>
                      )}
                    </p>
                  </div>
                  {paymentData.transactionRef && (
                    <div className="col-12">
                      <label className="form-label text-muted">Transaction Reference</label>
                      <p className="form-control-plaintext">{paymentData.transactionRef}</p>
                    </div>
                  )}
                  {(paymentData.remarks || paymentData.raw) && (
                    <div className="col-12">
                      <label className="form-label text-muted">BSE / Bank Remarks</label>
                      <p className="form-control-plaintext small">{paymentData.remarks || paymentData.raw}</p>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-muted">Last Updated</label>
                    <p className="form-control-plaintext">{paymentData.lastUpdated || new Date().toLocaleString()}</p>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                  {paymentData.status === "SUCCESS" && (
                    <>
                      <Link to="/user/order-status" state={{ clientCode: resolvedClientCode, orderNo: resolvedOrderNo }} className="btn btn-primary">Order Status</Link>
                      <Link to="/user" className="btn btn-outline-primary">Back to Users</Link>
                      <Link to="/dashboard" className="btn btn-outline-secondary">Dashboard</Link>
                    </>
                  )}
                  {paymentData.status === "PENDING" && (
                    <>
                      <button className="btn btn-primary" onClick={handleRefresh} disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />}
                        Refresh
                      </button>
                      <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                      <small className="text-muted align-self-center ms-2">
                        Auto-refreshing every {POLL_INTERVAL_MS / 60000} min
                      </small>
                    </>
                  )}
                  {(paymentData.status === "FAILED" || paymentData.status === "ERROR") && (
                    <div className="w-100">
                      <div className="mb-3">
                        <label className="form-label small text-muted">Payable Amount (for Retry)</label>
                        <input
                          type="number"
                          className="form-control"
                          style={{ maxWidth: "180px" }}
                          placeholder="e.g. 10000"
                          value={retryAmount || state?.amount || searchParams.get("amount") || ""}
                          onChange={(e) => setRetryAmount(e.target.value)}
                          min="1"
                          step="0.01"
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-warning" onClick={handleRetryPayment}>
                          <i className="fas fa-redo me-1" /> Retry Payment
                        </button>
                        <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                      </div>
                    </div>
                  )}
                  {paymentData.status === "UNKNOWN" && (
                    <>
                      <button className="btn btn-outline-primary" onClick={handleRefresh} disabled={loading}>
                        Refresh
                      </button>
                      <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
